/**
 * Epilogue: moves .outro and .contest elements into the game screen
 * as scene-wrapper elements, triggered by the story engine when it
 * encounters epilogue-type scenes in the scenes array.
 * The .authors block is appended after the outro only.
 */

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { getAudioManager } from '../audio'

gsap.registerPlugin(ScrollTrigger)

export interface Epilogue {
  appendElement(sceneId: string, elementSelector: string, container: HTMLElement): void
  remove(): void
}

export function setupEpilogue(gameScreen: HTMLElement, outroSound?: string): Epilogue {
  const app = gameScreen.parentElement!
  const appendedElements: { el: HTMLElement; sceneWrapper: HTMLDivElement }[] = []
  let triggers: ScrollTrigger[] = []
  let outroSoundPlayed = false
  let outroScrollHandler: (() => void) | null = null
  let outroParagraphRef: Element | null = null

  function checkOutroVisibility() {
    if (outroSoundPlayed || !outroParagraphRef) return
    const rect = outroParagraphRef.getBoundingClientRect()
    if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
      outroSoundPlayed = true
      getAudioManager().playGlobalSound(outroSound!)
      removeOutroScrollHandler()
    }
  }

  function removeOutroScrollHandler() {
    if (outroScrollHandler) {
      window.removeEventListener('scroll', outroScrollHandler)
      outroScrollHandler = null
    }
    outroParagraphRef = null
  }

  function appendElement(sceneId: string, elementSelector: string, container: HTMLElement): void {
    const el = app.querySelector(elementSelector) as HTMLElement | null
    if (!el) {
      console.error(`[Epilogue] Element "${elementSelector}" not found`)
      return
    }

    if (appendedElements.some((a) => a.el === el)) return

    const sceneWrapper = document.createElement('div')
    sceneWrapper.className = 'scene-wrapper epilogue-scene-wrapper'
    sceneWrapper.setAttribute('data-scene', sceneId)
    sceneWrapper.appendChild(el)

    let authorsEl: HTMLElement | null = null
    if (sceneId === 'outro') {
      authorsEl = app.querySelector(':scope > .authors') as HTMLElement | null
      if (authorsEl) {
        sceneWrapper.appendChild(authorsEl)
        authorsEl.classList.remove('hidden')
      }
    }

    container.appendChild(sceneWrapper)
    el.classList.remove('hidden')

    const fadeTargets: HTMLElement[] = [el]
    if (authorsEl) fadeTargets.push(authorsEl)
    gsap.set(fadeTargets, { opacity: 0 })
    const trigger = ScrollTrigger.create({
      trigger: el,
      start: 'top 100%',
      end: 'top 50%',
      once: true,
      invalidateOnRefresh: true,
      onEnter: () => {
        gsap.to(fadeTargets, { opacity: 1, duration: 0.8, ease: 'power2.out' })
      },
    })
    triggers.push(trigger)

    if (sceneId === 'outro' && outroSound && !outroSoundPlayed) {
      const outroParagraph = el.querySelector('.outro__logos + p')
      if (outroParagraph) {
        removeOutroScrollHandler()
        outroParagraphRef = outroParagraph
        outroScrollHandler = () => checkOutroVisibility()
        window.addEventListener('scroll', outroScrollHandler, { passive: true })
      }
    }

    appendedElements.push({ el, sceneWrapper })
    ScrollTrigger.refresh()
  }

  function remove(): void {
    if (appendedElements.length === 0) return

    removeOutroScrollHandler()

    triggers.forEach((t) => t.kill())
    triggers = []

    for (const { el, sceneWrapper } of appendedElements) {
      const authorsInWrapper = sceneWrapper.querySelector(':scope > .authors') as HTMLElement | null
      gsap.set(el, { clearProps: 'opacity' })
      el.classList.add('hidden')
      app.appendChild(el)
      if (authorsInWrapper) {
        gsap.set(authorsInWrapper, { clearProps: 'opacity' })
        authorsInWrapper.classList.add('hidden')
        app.appendChild(authorsInWrapper)
      }
      sceneWrapper.remove()
    }

    appendedElements.length = 0
  }

  return { appendElement, remove }
}
